import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building, FileText, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import { crmApi, type CrmQuote } from '../../services/api';
import { useCrmSellerStore } from '../../store/useCrmSellerStore';
import type { WorkItem } from '../../types';

/**
 * The commercial half of an opportunity: who is selling, how much, how likely,
 * when, and with whom — plus the quotes raised against it.
 *
 * These were set once at conversion and then had nowhere to be edited; the
 * slideout showed story points and sprints instead. Saved through the CRM's
 * own route, which checks the seller is one of our companies and keeps the
 * types the forecast relies on (integer minor units, a 0–100 probability).
 */

const LABEL = 'text-[10px] uppercase tracking-wider text-gray-400';
const FIELD = 'w-full px-2 py-1.5 text-sm rounded-lg border border-gray-300 ' +
  'dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 ' +
  'dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-accent';

const SOURCES = ['inbound', 'outbound', 'referral', 'expansion', 'rfp'];

function money(minor: number, currency = 'USD'): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency', currency, maximumFractionDigits: 2,
    }).format((minor || 0) / 100);
  } catch {
    return `${((minor || 0) / 100).toFixed(2)} ${currency}`;
  }
}

interface Props {
  item: WorkItem;
  /** Called with the fresh item after a save, so the slideout re-renders. */
  onSaved: () => void;
}

export default function OpportunityDeal({ item, onSaved }: Props) {
  const navigate = useNavigate();
  const sellers = useCrmSellerStore((s) => s.sellers);
  const loadSellers = useCrmSellerStore((s) => s.loadSellers);
  const [quotes, setQuotes] = useState<CrmQuote[]>([]);

  useEffect(() => { loadSellers(); }, [loadSellers]);
  useEffect(() => {
    crmApi.listQuotes(item._id).then((r) => setQuotes(r.quotes || [])).catch(() => setQuotes([]));
  }, [item._id]);

  const save = async (fields: Record<string, any>) => {
    try {
      await crmApi.updateOpportunity(item._id, fields);
      onSaved();
    } catch (err: any) {
      toast.error(err.message || 'Could not save', { duration: 9000 });
    }
  };

  /** Typed as a decimal string; becomes integer minor units once, here. */
  const saveAmount = (amount: string) => {
    const parsed = Number(amount.replace(/[^0-9.]/g, ''));
    if (amount && !Number.isFinite(parsed)) {
      toast.error('Value must be a number');
      return;
    }
    const minor = amount ? Math.round(parsed * 100) : 0;
    if (minor !== (item.oppValueMinor || 0)) save({ oppValueMinor: minor });
  };

  /** Save a text field on blur, only when it changed. */
  const text = (field: keyof WorkItem, label: string, type = 'text') => (
    <div>
      <label className={LABEL}>{label}</label>
      <input
        key={`${item._id}-${String(field)}-${String(item[field] ?? '')}`}
        type={type}
        className={FIELD}
        defaultValue={String(item[field] ?? '')}
        onBlur={(e) => {
          if (e.target.value !== String(item[field] ?? '')) save({ [field]: e.target.value });
        }}
      />
    </div>
  );

  const terminal = ['won', 'lost', 'disqualified'].includes(item.status);

  return (
    <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 space-y-3">
      {/* Renaming a deal. The quotes keep the title they were raised with —
          an issued quote is a record of what was sent. */}
      <div>
        <label className={LABEL}>Opportunity name</label>
        <input
          key={`${item._id}-title-${item.title}`}
          className={FIELD}
          defaultValue={item.title}
          onBlur={(e) => {
            const name = e.target.value.trim();
            if (!name) { e.target.value = item.title; toast.error('An opportunity needs a name'); return; }
            if (name !== item.title) save({ title: name });
          }}
          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        />
      </div>
      <div>
        <label className={LABEL}>
          <Building size={10} className="inline mr-1" />Selling as
        </label>
        <select
          className={FIELD}
          value={item.oppSellerOrgId || ''}
          onChange={(e) => save({ oppSellerOrgId: e.target.value })}
        >
          <option value="">— not set —</option>
          {sellers.map((s) => (
            <option key={s._id} value={s.orgId}>{s.name}</option>
          ))}
        </select>
        {!item.oppSellerOrgId && (
          <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-500">
            No selling company — quotes on this deal will not say who issues them.
          </p>
        )}
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2">
          <label className={LABEL}>Value</label>
          <input className={FIELD} inputMode="decimal" placeholder="0.00"
            key={`${item._id}-value-${item.oppValueMinor ?? 0}`}
            defaultValue={item.oppValueMinor ? (item.oppValueMinor / 100).toFixed(2) : ''}
            onBlur={(e) => saveAmount(e.target.value)} />
        </div>
        {text('oppCurrency', 'Currency')}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={LABEL}>Probability %</label>
          <input
            key={`${item._id}-prob-${item.oppProbability ?? 0}`}
            className={FIELD} type="number" min={0} max={100}
            defaultValue={item.oppProbability ?? 0}
            onBlur={(e) => {
              const v = Math.max(0, Math.min(100, Math.round(Number(e.target.value) || 0)));
              if (v !== (item.oppProbability ?? 0)) save({ oppProbability: v });
            }}
          />
        </div>
        {text('oppExpectedCloseDate', 'Expected close', 'date')}
      </div>

      <div className="grid grid-cols-2 gap-3">
        {text('oppContactName', 'Contact')}
        {text('oppContactEmail', 'Contact email', 'email')}
      </div>

      <div className="grid grid-cols-2 gap-3">
        {text('oppAccount', 'Account name')}
        <div>
          <label className={LABEL}>Source</label>
          <select className={FIELD} value={item.oppSource || ''}
            onChange={(e) => save({ oppSource: e.target.value })}>
            <option value="">—</option>
            {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {(terminal || item.oppLostReason) && text('oppLostReason', 'Lost / not valid — reason')}

      {/* Quotes against this deal. A quote takes its client, partner and
          seller from here, so this is where one is started. */}
      <div className="pt-1">
        <div className="flex items-center justify-between mb-1.5">
          <span className={LABEL}>
            <FileText size={10} className="inline mr-1" />Quotes ({quotes.length})
          </span>
          <button
            onClick={() => navigate(`/crm/quotes?opportunityId=${encodeURIComponent(item._id)}`)}
            className="flex items-center gap-1 text-xs text-accent hover:underline"
          >
            <Plus size={12} /> New quote
          </button>
        </div>
        {quotes.length === 0 ? (
          <p className="text-xs text-gray-400">No quotes yet.</p>
        ) : (
          <div className="space-y-1">
            {quotes.map((q) => (
              <button
                key={q._id}
                onClick={() => navigate('/crm/quotes')}
                className="w-full flex items-center justify-between gap-2 rounded-lg border border-gray-200 dark:border-gray-700 px-2.5 py-1.5 text-xs hover:border-accent/50"
              >
                <span className="font-mono text-gray-500">{q.quoteNumber} v{q.version}</span>
                <span className="text-gray-500">{q.status}</span>
                <span className="font-medium text-gray-900 dark:text-gray-100 tabular-nums">
                  {money(q.totalMinor, q.currency)}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
